/**
 * Тип поля — ячейка 1, три года истории NDVI. Отдельный вопрос: не «убрано ли поле», а «что
 * это за поле».
 *
 * С 2026-09-04 у него НЕТ своего блока в колонке (UX_SPEC §2, §3.4). Ответ — бейдж
 * «[Пашня] / [Луг] / [Пашня и луг]» в шапке поля: одно слово, которое меняет трактовку
 * схода растительности, а не третий заголовок над карточкой ответа. Пока тип не посчитан,
 * бейджа нет и ни слова о нём тоже: «Тип поля не определён» было заголовком об отсутствии
 * там, где отсутствие ничего не меняет. Кнопка пересчёта и дата расчёта уехали в блок
 * «Чем посчитан ответ», а всё, что ниже, — счётчики сезонов и интервалы `V01` — прибор, и
 * этот компонент рисует ровно их, в служебной панели.
 *
 * Числа — моношириной и в столбик, как показания прибора (визуальный контракт).
 */

import { T } from "../strings";
import type { FieldUse as FieldUseData, VegetationInterval } from "../api/types";
import { formatDate, formatDateTime, integer } from "../lib/format";

interface Props {
  /** null = никогда не считалось для этого поля: показывать нечего. */
  fieldUse: FieldUseData | null;
}

const intervalClassLabel = (interval: VegetationInterval): string => {
  const code = interval.interval_class ?? "";
  const known = T.fieldUse.intervals.classes as Record<string, string | undefined>;
  return known[code] ?? interval.interval_class_ru ?? code;
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </>
);

function Intervals({ intervals }: { intervals: VegetationInterval[] }) {
  if (intervals.length === 0) {
    return <p className="k-hint">{T.fieldUse.intervals.none}</p>;
  }
  return (
    <div className="k-log" data-testid="field-use-intervals">
      {intervals.map((interval, index) => (
        <div className="k-logrow" key={interval.interval_id ?? index}>
          <span className="k-logrow__date">{formatDate(interval.first_active_date)}</span>
          <span className="k-logrow__reason">
            {/* Метка ноутбука ("V01") — по ней агроном ссылается на интервал. */}
            {interval.interval_id !== null && (
              <span className="k-logrow__numbers">{interval.interval_id} </span>
            )}
            {intervalClassLabel(interval)}
            <span className="k-logrow__numbers">
              {" "}
              {formatDate(interval.last_active_date)} ·{" "}
              {interval.active_duration_days === null
                ? T.common.noData
                : T.fieldUse.intervals.duration(interval.active_duration_days)}
              {interval.peak_count === null
                ? ""
                : ` · ${T.fieldUse.intervals.peaks(interval.peak_count)}`}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function FieldUse({ fieldUse }: Props) {
  // Пока считать нечего — нечего и показывать: приглашение и кнопка пересчёта стоят на
  // продуктовой стороне, в блоке «Чем посчитан ответ».
  if (fieldUse === null) return null;

  return (
    <div data-testid="field-use-numbers">
      <p className="k-hint k-mono">
        {T.fieldUse.computedAt(formatDateTime(fieldUse.computed_at))} ·{" "}
        {T.fieldUse.window(fieldUse.history_years, formatDate(fieldUse.analysis_date))}
      </p>

      <dl className="k-readout">
        <Row
          label={T.fieldUse.numbers.arableSeasons}
          value={`${integer(fieldUse.arable_seasons_count)} · ${integer(
            fieldUse.arable_days,
          )} ${T.fieldUse.numbers.arableDays}`}
        />
        <Row
          label={T.fieldUse.numbers.grasslikePeriods}
          value={`${integer(fieldUse.grasslike_periods_count)} · ${integer(
            fieldUse.grasslike_days,
          )} ${T.fieldUse.numbers.grasslikeDays}`}
        />
        <Row
          label={T.fieldUse.numbers.secondaryPeriods}
          value={`${integer(fieldUse.secondary_periods_count)} · ${integer(
            fieldUse.secondary_days,
          )} ${T.fieldUse.numbers.secondaryDays}`}
        />
        <Row
          label={T.fieldUse.numbers.peakCandidates}
          value={integer(fieldUse.peak_candidates_count)}
        />
        <Row
          label={T.fieldUse.numbers.rejectedPeaks}
          value={integer(fieldUse.rejected_peaks_count)}
        />
      </dl>

      <details className="k-details">
        <summary>
          {T.fieldUse.intervals.title} ·{" "}
          {T.fieldUse.intervals.count(fieldUse.vegetation_intervals.length)}
        </summary>
        <Intervals intervals={fieldUse.vegetation_intervals} />
      </details>
    </div>
  );
}
